/**
 * Remembering where the user was when their session ended, so signing back in
 * returns them there instead of dumping them on a default page.
 *
 * The value is attacker-reachable — anything that can write localStorage can set
 * it, and the app navigates wherever it points after a successful login. So it
 * is validated on the way out, not only on the way in: a stored destination is
 * treated as untrusted input every time it is read.
 */

const RETURN_TO_KEY = 'session_return_to';

/**
 * A destination older than this is stale: the user has moved on, and sending
 * them somewhere they asked for hours ago is surprising rather than helpful.
 */
const RETURN_TO_TTL_MS = 10 * 60 * 1000;

/** Query params that must never be persisted, even briefly. */
const SENSITIVE_PARAMS = ['jwt', 'token', 'auth-token', 'password', 'code', 'session_id', 'email'];

/** Paths that make no sense to return to — you would land back where you started. */
const NON_RETURNABLE_PREFIXES = ['/auth', '/resetPassword', '/logout'];

/**
 * Whether a path is a safe in-app destination.
 *
 * Rejects anything that could leave the origin. The subtle one is '//evil.com':
 * it starts with '/', so a naive check passes it, and the browser reads it as a
 * protocol-relative URL and navigates off-site. Same for '/\evil.com', which
 * some browsers normalise to the same thing.
 */
export const isSafeInternalPath = (path) => {
  if (typeof path !== 'string' || !path) return false;
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//') || path.startsWith('/\\')) return false;
  // A scheme before the first slash means it is not the relative path it claims.
  if (/^[a-z][a-z0-9+.-]*:/i.test(path)) return false;
  if (NON_RETURNABLE_PREFIXES.some((prefix) => path.startsWith(prefix))) return false;
  return true;
};

const stripSensitiveParams = (search) => {
  if (!search) return '';
  const params = new URLSearchParams(search);
  SENSITIVE_PARAMS.forEach((key) => params.delete(key));
  const cleaned = params.toString();
  return cleaned ? `?${cleaned}` : '';
};

/**
 * Stores where to come back to. Router state is deliberately not preserved: it
 * can hold anything a view put there, and it does not survive the hard
 * navigation the sign-out performs anyway.
 */
export const saveReturnTo = (location) => {
  if (!location) return;

  const path = `${location.pathname || ''}${stripSensitiveParams(location.search)}`;
  if (!isSafeInternalPath(path)) return;

  try {
    localStorage.setItem(RETURN_TO_KEY, JSON.stringify({ path, savedAt: Date.now() }));
  } catch {
    // Returning to the default page is a mild degradation, not a failure.
  }
};

/**
 * Reads the destination and clears it, so it is used exactly once. Leaving it
 * behind would send the user back there on every later login.
 *
 * @returns {string|null} a safe internal path, or null when there is nothing
 * usable — including a stored value that is stale, malformed, or tampered with.
 */
export const consumeReturnTo = () => {
  let raw;
  try {
    raw = localStorage.getItem(RETURN_TO_KEY);
    localStorage.removeItem(RETURN_TO_KEY);
  } catch {
    return null;
  }

  if (!raw) return null;

  try {
    const { path, savedAt } = JSON.parse(raw);
    if (!savedAt || Date.now() - savedAt > RETURN_TO_TTL_MS) return null;
    // Re-validated on read: what went in is not necessarily what comes out.
    return isSafeInternalPath(path) ? path : null;
  } catch {
    return null;
  }
};

export const clearReturnTo = () => {
  try {
    localStorage.removeItem(RETURN_TO_KEY);
  } catch {
    // Nothing to do.
  }
};

export { RETURN_TO_KEY, RETURN_TO_TTL_MS };
